import React, { useState, useEffect } from 'react';
import './ChordProgressionGenerator.css';
import { progressions } from '../data/chords';
import ChordDisplay from './ChordDisplay';
import { 
  initAudio, 
  playChord, 
  playProgression, 
  stopAudio, 
  rhythmPatterns, 
  instrumentSounds,
  changeInstrument,
  getCurrentInstrument,
  cleanupAudio,
  checkAndRecoverAudioEngine
} from '../utils/audioUtils';

function ChordProgressionGenerator() {
  const [genre, setGenre] = useState('Pop');
  const [selectedProgression, setSelectedProgression] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentChord, setCurrentChord] = useState(null);
  const [tempo, setTempo] = useState(120);
  const [isMuted, setIsMuted] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [complexityFilter, setComplexityFilter] = useState(0); // 0 means all complexities
  const [rhythmPattern, setRhythmPattern] = useState('Even');
  const [instrument, setInstrument] = useState('Piano');
  const [isAudioInitializing, setIsAudioInitializing] = useState(false);
  const [playbackError, setPlaybackError] = useState(null);

  // Don't initialize audio on component mount, but on first user interaction
  const ensureAudioInitialized = async () => {
    if (!audioReady) {
      setIsAudioInitializing(true);
      try {
        const success = await initAudio();
        setAudioReady(success);
        setIsAudioInitializing(false);
        if (!success) {
          setPlaybackError('Audio engine initialization failed, please check browser permissions or refresh the page');
        } else {
          setPlaybackError(null);
        }
        return success;
      } catch (error) {
        console.error("Audio initialization error:", error);
        setIsAudioInitializing(false);
        setPlaybackError('Audio engine error: ' + (error.message || 'Unknown error'));
        return false;
      }
    }
    return true;
  };

  // 自动重试音频初始化的辅助函数
  const retryAudioInitialization = async (maxRetries = 3) => {
    let retries = 0;
    let success = false;

    while (retries < maxRetries && !success) {
      success = await initAudio();
      if (!success) {
        retries++;
        // 指数退避策略: 每次失败后等待时间翻倍
        await new Promise(resolve => setTimeout(resolve, 300 * Math.pow(2, retries))); 
      }
    }

    return success;
  };

  // When the selected style changes, reset the selected chord progression
  useEffect(() => {
    setSelectedProgression(null);
  }, [genre]);
  
  // Cleanup audio on component unmount
  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, []);
  
  // 定期检查音频引擎健康度
  useEffect(() => {
    // 仅在需要时进行健康检查
    if (audioReady) {
      const healthCheck = async () => {
        const isHealthy = await checkAndRecoverAudioEngine();
        if (!isHealthy && audioReady) {
          setAudioReady(false);
          setPlaybackError('Audio engine state is abnormal. Recovery attempted. If the problem persists, please refresh the page.');
        }
      };

      // 只在激活时进行健康检查
      if (selectedProgression) {
        healthCheck();
      }
    }
  }, [selectedProgression, audioReady]);
  
  // Handle instrument change
  const handleInstrumentChange = async (instrumentName) => {
    try {
      if (!audioReady) {
        await ensureAudioInitialized();
      }
      
      if (changeInstrument(instrumentName)) {
        setInstrument(instrumentName);
        // 尝试播放一个单音以测试新乐器
        if (audioReady && !isMuted && selectedProgression) {
          playChord(selectedProgression.chords[0], '8n');
        }
      }
    } catch (error) {
      console.error("Failed to change instrument:", error);
      setPlaybackError("Failed to change instrument");
    }
  };

  // Handle playing all chords with better timed chord highlighting
  const handlePlayProgression = async () => {
    if (!selectedProgression || isPlaying) return;
    setPlaybackError(null);

    // Ensure audio is initialized
    setIsAudioInitializing(true);
    let audioInitialized = await ensureAudioInitialized();
    
    if (!audioInitialized) {
      // 尝试重试初始化
      audioInitialized = await retryAudioInitialization();
    }
    
    setIsAudioInitializing(false);
    
    if (!audioInitialized) {
      setPlaybackError('Cannot play: Audio engine initialization failed, please check browser permissions or refresh the page');
      return;
    }

    setIsPlaying(true);
    
    // Get chord sequence
    const chords = selectedProgression.chords;
    
    try {
      // 计算节奏模式总持续时间
      const calculatePatternDuration = (patternName, tempo) => {
        const pattern = rhythmPatterns[patternName]?.pattern || ['2n'];
        const quarterNoteDuration = 60 / tempo;
        
        let totalDuration = 0;
        
        // 对于节奏模式，一个和弦可能有多个节奏单位
        pattern.forEach(notation => {
          switch(notation) {
            case '1n': totalDuration += 4 * quarterNoteDuration; break;
            case '2n.': totalDuration += 3 * quarterNoteDuration; break;
            case '2n': totalDuration += 2 * quarterNoteDuration; break;  
            case '4n.': totalDuration += 1.5 * quarterNoteDuration; break;
            case '4n': totalDuration += quarterNoteDuration; break;
            case '8n': totalDuration += 0.5 * quarterNoteDuration; break;
            case '16n': totalDuration += 0.25 * quarterNoteDuration; break;
            default: totalDuration += 2 * quarterNoteDuration; // 默认半音符
          }
        });
        
        return totalDuration;
      };
      
      // 计算每个和弦的预计持续时间（秒）
      let secondsPerChord;
      
      if (rhythmPattern === 'Arpeggio') {
        // 琶音模式每个和弦约需0.5-1秒 (取决于和弦中音符数量和速度)
        // 假设平均4个音符的和弦
        const notesPerChord = 4;
        const quarterNoteDuration = 60 / tempo;
        secondsPerChord = notesPerChord * quarterNoteDuration * 0.25; // 16分音符间隔
      } else {
        // 计算一个完整节奏模式的持续时间
        const patternDuration = calculatePatternDuration(rhythmPattern, tempo);
        secondsPerChord = patternDuration; // 这是一个和弦的完整节奏周期
      }
      
      // 确保每个和弦至少有最小持续时间
      secondsPerChord = Math.max(secondsPerChord, 0.5);
      
      // 设置可取消的定时器数组
      const timers = [];
      
      // 设置和弦高亮显示的定时器 - 考虑每个和弦可能包含多个节奏单位
      let currentTimeOffset = 0;
      
      for (let i = 0; i < chords.length; i++) {
        const timerId = setTimeout(() => {
          if (isPlaying) { // 只有在仍在播放状态时才更新高亮
            setCurrentChord(chords[i]);
          }
        }, currentTimeOffset * 1000);
        
        timers.push(timerId);
        
        // 增加当前时间偏移
        currentTimeOffset += secondsPerChord;
      }
      
      // 最后一个和弦结束后停止高亮
      const finalTimerId = setTimeout(() => {
        setCurrentChord(null);
        setIsPlaying(false);
      }, currentTimeOffset * 1000 + 300);
      
      timers.push(finalTimerId);
      
      // 播放和弦进行
      if (!isMuted) {
        // 直接传递所有参数确保正确应用节奏模式
        const playbackDuration = await playProgression(
          chords, 
          tempo, 
          rhythmPattern,
          {
            loop: false,
            octave: 4,
            arpeggioDirection: 'up'
          }
        );
        
        // 如果返回的持续时间与预计的不一致，调整最终计时器
        if (playbackDuration && Math.abs(playbackDuration - currentTimeOffset) > 0.5) {
          // 清除最后的计时器
          clearTimeout(finalTimerId);
          
          // 设置新的结束计时器
          setTimeout(() => {
            setCurrentChord(null);
            setIsPlaying(false);
          }, playbackDuration * 1000 + 300);
        }
      } else {
        // 如果静音，则只等待相应的时间
        await new Promise(resolve => 
          setTimeout(resolve, currentTimeOffset * 1000)
        );
      }
      
      // 清理函数 - 用于组件卸载或播放停止时
      return () => {
        timers.forEach(id => clearTimeout(id));
      };
    } catch (error) {
      console.error('Error during playback:', error);
      setPlaybackError('Playback error: ' + (error.message || 'Unknown error'));
      stopAudio();
    } finally {
      setIsPlaying(false);
    }
  };

  // Handle playing a single chord with better feedback
  const handlePlayChord = async (chord) => {
    if (isPlaying) return; // 不要在整体播放过程中播放单个和弦
    
    // 确保音频已初始化
    if (!audioReady) {
      const success = await ensureAudioInitialized();
      if (!success || isMuted) return;
    }
    
    // 视觉反馈
    setCurrentChord(chord);
    setTimeout(() => setCurrentChord(null), 800); // 0.8秒后清除高亮
    
    try {
      await playChord(chord);
    } catch (error) {
      console.error('Error playing single chord:', error);
    }
  };

  // Stop playback with better cleanup
  const handleStopPlaying = () => {
    stopAudio();
    setIsPlaying(false);
    setCurrentChord(null);
    setPlaybackError(null);
  };

  // Randomly select a chord progression
  const handleRandomProgression = () => {
    // Get filtered progressions
    const filteredProgressions = getFilteredProgressions();
    
    if (filteredProgressions.length > 0) {
      const randomIndex = Math.floor(Math.random() * filteredProgressions.length);
      setSelectedProgression(filteredProgressions[randomIndex]);
    }
  };

  // Get complexity label
  const getComplexityLabel = (complexity) => {
    switch(complexity) {
      case 1: return 'Simple';
      case 2: return 'Medium';
      case 3: return 'Complex';
      default: return 'Unknown';
    }
  };

  // Filter progressions based on complexity
  const getFilteredProgressions = () => {
    if (complexityFilter === 0) {
      return progressions[genre];
    } else {
      return progressions[genre].filter(prog => prog.complexity === complexityFilter);
    }
  };

  return (
    <div className="chord-progression-generator">
      <div className="control-panel card">
        <h2>Chord Progression Generator</h2>
        
        <div className="controls">
          <div className="control-group">
            <label htmlFor="genre-select">Music Genre:</label>
            <select 
              id="genre-select" 
              value={genre} 
              onChange={(e) => setGenre(e.target.value)}
            >
              {Object.keys(progressions).map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label htmlFor="complexity-filter">Complexity:</label>
            <select
              id="complexity-filter"
              value={complexityFilter}
              onChange={(e) => setComplexityFilter(parseInt(e.target.value))}
            >
              <option value="0">All Levels</option>
              <option value="1">Easy</option>
              <option value="2">Medium</option>
              <option value="3">Hard</option>
            </select>
          </div>
          
          <div className="control-group">
            <label htmlFor="rhythm-pattern">Rhythm Pattern:</label>
            <select
              id="rhythm-pattern"
              value={rhythmPattern}
              onChange={(e) => setRhythmPattern(e.target.value)}
              disabled={isPlaying}
            >
              {Object.keys(rhythmPatterns).map((pattern) => (
                <option key={pattern} value={pattern}>{pattern}</option>
              ))}
            </select>
            <div className="tooltip">
              <span className="tooltip-icon">?</span>
              <span className="tooltip-text">{rhythmPatterns[rhythmPattern].description}</span>
            </div>
          </div>
          
          <div className="control-group">
            <label htmlFor="instrument-select">Instrument:</label>
            <select
              id="instrument-select"
              value={instrument}
              onChange={(e) => handleInstrumentChange(e.target.value)}
              disabled={isPlaying}
            >
              {Object.keys(instrumentSounds).map((inst) => (
                <option key={inst} value={inst}>{inst}</option>
              ))}
            </select>
          </div>
          
          <div className="control-group">
            <label htmlFor="tempo-control">Tempo (BPM):</label>
            <input 
              id="tempo-control" 
              type="range" 
              min="60" 
              max="180" 
              value={tempo} 
              onChange={(e) => setTempo(parseInt(e.target.value))}
              disabled={isPlaying}
            />
            <span>{tempo} BPM</span>
          </div>
          
          <div className="control-group">
            <label htmlFor="mute-toggle">Mute:</label>
            <input 
              id="mute-toggle" 
              type="checkbox" 
              checked={isMuted} 
              onChange={() => setIsMuted(!isMuted)}
            />
          </div>
        </div>
        
        {playbackError && (
          <div className="error-message">
            <p>{playbackError}</p>
            <button onClick={() => setPlaybackError(null)} className="btn-small">
              Close
            </button>
          </div>
        )}
        
        <div className="action-buttons">
          <button 
            className="btn" 
            onClick={handleRandomProgression}
            disabled={isAudioInitializing}
          >
            Random Chord Progression
          </button>
          
          <button 
            className="btn btn-secondary" 
            onClick={handlePlayProgression}
            disabled={!selectedProgression || isPlaying || isAudioInitializing}
          >
            {isAudioInitializing ? 'Initializing Audio...' : 
             isPlaying ? 'Playing...' : 'Play Chord Progression'}
          </button>
          
          {isPlaying && (
            <button 
              className="btn btn-danger" 
              onClick={handleStopPlaying}
            >
              Stop
            </button>
          )}
        </div>
        
        <div className="playback-tips">
          <p>Tip: Click on individual chords to preview. Try different rhythm patterns and instruments for a richer musical experience.</p>
        </div>
      </div>
      
      {selectedProgression && (
        <div className="progression-display card">
          <div className="progression-info">
            <h3>{selectedProgression.name}</h3>
            <p>{selectedProgression.description}</p>
            <div className="complexity-badge">
              {getComplexityLabel(selectedProgression.complexity)}
            </div>
          </div>
          
          <div className="playback-info">
            <div className="playback-detail">
              <span className="detail-label">Rhythm:</span>
              <span className="detail-value">{rhythmPattern}</span>
            </div>
            <div className="playback-detail">
              <span className="detail-label">Instrument:</span>
              <span className="detail-value">{instrument}</span>
            </div>
            <div className="playback-detail">
              <span className="detail-label">Tempo:</span>
              <span className="detail-value">{tempo} BPM</span>
            </div>
          </div>
          
          <div className="chords-container">
            {selectedProgression.chords.map((chord, index) => (
              <div 
                key={index} 
                onClick={() => handlePlayChord(chord)}
                className={`chord-item ${chord === currentChord ? 'playing' : ''}`}
              >
                <ChordDisplay 
                  chord={chord} 
                  isActive={chord === currentChord}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="progression-list card">
        <h3>Recommended {genre} Chord Progressions</h3>
        <div className="progression-options">
          {getFilteredProgressions().map((prog, index) => (
            <div 
              key={index}
              className={`progression-option ${selectedProgression === prog ? 'selected' : ''}`}
              onClick={() => setSelectedProgression(prog)}
            >
              <h4>{prog.name}</h4>
              <div className="progression-preview">
                {prog.chords.join(' - ')}
              </div>
              <div className="progression-details">
                <p>{prog.description}</p>
                <span className="complexity-tag">
                  {getComplexityLabel(prog.complexity)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ChordProgressionGenerator; 